import { z } from "zod";
import { idSchema, idempotencyKeySchema, instantSchema } from "./common.js";
import { photoRoleSchema } from "./panel-creation.js";

/**
 * Phase 12g — input/output schemas for the `photos.*` procedures.
 *
 * The capture pipeline:
 * 1. Client asks `photos.uploadUrl({ photoId, contentType })` → server
 *    returns a presigned R2 PUT URL with content-type + max-size
 *    constraints sealed into the URL. The Lite photo-count cap is
 *    checked here.
 * 2. Client PUTs the bytes directly to R2. Server-side EXIF stripping
 *    runs in the R2 adapter's pre-`put` hook (Phase 12a), since the
 *    client cannot be trusted to scrub.
 * 3. Client calls `photos.metadata({ photoId, attach, capturedAt })`
 *    to record the parent aggregate + capture timestamp.
 * 4. To remove: `photos.delete({ photoId })` deletes the R2 object and
 *    removes the metadata row (frees a Lite slot).
 *
 * The `photoId` is *client-generated* (UUID) per the Phase 9f decision
 * that aggregate ids are minted on the client; the server only
 * verifies non-emptiness here.
 */

/**
 * Photo content types the upload endpoint will presign. Mirrors the
 * Phase 12a EXIF-stripping contract: JPEG goes through the strip path;
 * PNG nameplates carry no EXIF; HEIC capture is converted to JPEG
 * client-side in Phase 13e before upload.
 */
export const photoContentTypeSchema = z.enum([
  "image/jpeg",
  "image/png",
]);
export type PhotoContentType = z.infer<typeof photoContentTypeSchema>;

export const photosUploadUrlInputSchema = z.object({
  photoId: idSchema,
  contentType: photoContentTypeSchema.default("image/jpeg"),
});
export type PhotosUploadUrlInput = z.infer<typeof photosUploadUrlInputSchema>;

/**
 * Output of `photos.uploadUrl`. The Phase 13 client uses `url` as the
 * presigned PUT target and `maxBytes` / `contentType` to gate the
 * upload locally (a 10MB photo that the client knows is over-cap can
 * be downsampled before the round trip).
 */
export const photosUploadUrlOutputSchema = z.object({
  url: z.string().min(1),
  photoId: idSchema,
  ttlSeconds: z.number().int().positive(),
  maxBytes: z.number().int().positive(),
  contentType: photoContentTypeSchema,
});
export type PhotosUploadUrlOutput = z.infer<typeof photosUploadUrlOutputSchema>;

/**
 * Attach target for the metadata row — discriminated union mirroring
 * the per-aggregate photo placement. A photo lives on exactly one of:
 * a site (general site photo), a panel (the panel cover / panel
 * interior), or a device (nameplate / installation photo). Mirrors the
 * Phase 12d `PhotoAttachTarget` discriminator without the `circuit`
 * arm — `photos.metadata` only records the *uploaded* photo's parent,
 * not the per-aggregate attachment relationships that
 * `panels.attachPhoto` manages.
 */
export const photosAttachTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("site"), siteId: idSchema }),
  z.object({ kind: z.literal("panel"), panelId: idSchema }),
  z.object({ kind: z.literal("device"), deviceId: idSchema }),
]);
export type PhotosAttachTarget = z.infer<typeof photosAttachTargetSchema>;

export const photosMetadataInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  photoId: idSchema,
  attach: photosAttachTargetSchema,
  capturedAt: instantSchema,
  /**
   * Canonical role per `PhotoRole` (deadFrontOn, deadFrontOff, …).
   * Optional because earlier callers (the pre-N-01 photo flow) don't
   * supply one. The new-panel capture flow always sets a role here at
   * upload time; the post-capture review screen can change it later
   * via `photos.updateRole`.
   */
  role: photoRoleSchema.optional(),
  /**
   * Free-text note the user attached to the photo on the post-capture
   * review screen (tablet form factor only; phone defers notes to the
   * panel-detail gallery in a later phase).
   */
  note: z.string().max(2000).optional(),
});
export type PhotosMetadataInput = z.infer<typeof photosMetadataInputSchema>;

export const photosMetadataOutputSchema = z.object({
  photoId: idSchema,
  exifStripped: z.literal(true),
});
export type PhotosMetadataOutput = z.infer<typeof photosMetadataOutputSchema>;

export const photosDeleteInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  photoId: idSchema,
});
export type PhotosDeleteInput = z.infer<typeof photosDeleteInputSchema>;

export const photosDeleteOutputSchema = z.object({
  photoId: idSchema,
  deleted: z.boolean(),
});
export type PhotosDeleteOutput = z.infer<typeof photosDeleteOutputSchema>;
